import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { LogOut, Menu, Settings, X } from 'lucide-react';
import { Avatar, cn, Logo, useDesktopNotifications, useToast } from '@gbv/ui';
import { useAuth } from '../stores/auth';
import { api } from '../lib/api';
import { connectSocket, disconnectSocket, onServerEvent } from '../lib/socket';
import { FloatingChat } from './FloatingChat';
import { CLIENT_NAV } from './navigation';
import { useCrmAccess } from '../lib/crmAccess';

/**
 * Dark rail on the left, light workspace on the right.
 *
 * The owner opens this on a phone at least as often as a laptop, so below lg
 * the rail becomes a slide-in drawer rather than shrinking into unusable
 * icons.
 */
export function ClientShell({ children }: { children: ReactNode }) {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const user = useAuth((s) => s.user);
  const memberships = useAuth((s) => s.memberships);
  const activeOrgId = useAuth((s) => s.activeOrgId);
  const switchOrg = useAuth((s) => s.switchOrg);
  const logout = useAuth((s) => s.logout);
  const navigate = useNavigate();
  const isReadOnly = useAuth((s) => s.isReadOnly);

  const active = memberships.find((m) => m.orgId === activeOrgId);

  // The unread badge is read from the dashboard payload, which every screen
  // shares through the query cache — no extra request per navigation.
  const { data: unread } = useQuery({
    queryKey: ['client-unread', activeOrgId],
    queryFn: () => api.get<{ unreadMessages: number }>('/dashboard?period=today'),
    enabled: Boolean(activeOrgId),
    select: (d) => d.unreadMessages,
    refetchInterval: 60_000,
  });

  const selfId = useAuth((s) => s.user?.id);
  const toast = useToast();
  const queryClient = useQueryClient();
  const desktop = useDesktopNotifications('client');

  useEffect(() => {
    connectSocket();
    return () => disconnectSocket();
  }, []);

  /**
   * A reply from the team, or a file added to their account. The unread badge
   * already polls once a minute, which is fine for a number in the corner and
   * far too slow to be told someone answered you — so the event refreshes it
   * on arrival instead of waiting for the next tick.
   *
   * Away from the tab it is a desktop notification; with the tab open it is a
   * toast. The hook decides, so it is never both.
   */
  useEffect(
    () =>
      onServerEvent('notification:new', (payload) => {
        // Skip what this person just did themselves. The confirmation is
        // already on screen, and a second one describing it in the third
        // person reads like somebody else acted.
        if (payload.actorId && payload.actorId === selfId) return;

        void queryClient.invalidateQueries({ queryKey: ['client-unread'] });

        const shown = desktop.notify({
          title: payload.title,
          body: payload.body,
          link: payload.link,
          tag: payload.id,
        });

        if (!shown) toast.info(payload.title, payload.body);
      }),
    [queryClient, desktop, toast, selfId],
  );
  return (
    <div className="flex min-h-screen bg-auth-page">
      <Rail unread={unread ?? 0} orgName={active?.orgName ?? ''} className="hidden lg:flex" />

      <div className="flex min-w-0 flex-1 flex-col">
        {isReadOnly && (
          <div
            role="status"
            className="flex items-center gap-2 bg-bee px-4 py-1.5 text-xs font-semibold text-ink"
          >
            Viewing as {user?.name ?? 'this client'} — read only.
          </div>
        )}

        <header className="sticky top-0 z-30 flex h-topbar items-center gap-3 border-b border-line bg-white px-4 lg:px-6">
          <button
            type="button"
            onClick={() => setMobileNavOpen(true)}
            aria-label="Open navigation"
            className="rounded-control p-1.5 text-ink-muted transition-colors hover:bg-surface-sunken lg:hidden"
          >
            <Menu size={18} />
          </button>

          <div className="min-w-0">
            <p className="truncate text-sm font-bold text-ink">{active?.orgName}</p>
            <p className="truncate font-mono text-[10px] uppercase tracking-wider text-ink-soft">
              Client portal
            </p>
          </div>

          <div className="ml-auto">
            <DropdownMenu.Root>
              <DropdownMenu.Trigger className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-auth-gold focus-visible:ring-offset-2">
                <Avatar name={user?.name ?? '?'} src={user?.avatarUrl} size="sm" />
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  align="end"
                  sideOffset={6}
                  className="z-50 w-60 overflow-hidden rounded-card border border-line bg-white shadow-overlay"
                >
                  <div className="border-b border-line px-3 py-2.5">
                    <p className="truncate text-sm font-semibold text-ink">{user?.name}</p>
                    <p className="truncate text-xs text-ink-muted">{user?.email}</p>
                  </div>

                  {memberships.length > 1 && (
                    <div className="border-b border-line py-1">
                      <p className="px-3 py-1 font-mono text-[9px] uppercase tracking-widest text-ink-soft">
                        Your businesses
                      </p>
                      {memberships.map((m) => (
                        <DropdownMenu.Item
                          key={m.orgId}
                          onSelect={() => switchOrg(m.orgId)}
                          className="flex cursor-pointer items-center justify-between gap-2 px-3 py-2 text-sm outline-none data-[highlighted]:bg-surface-sunken"
                        >
                          <span className="truncate text-ink">{m.orgName}</span>
                          {m.orgId === activeOrgId && (
                            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-go" />
                          )}
                        </DropdownMenu.Item>
                      ))}
                    </div>
                  )}

                  <DropdownMenu.Item
                    onSelect={() => navigate('/account')}
                    className="flex cursor-pointer items-center gap-2 px-3 py-2.5 text-sm text-ink outline-none data-[highlighted]:bg-surface-sunken"
                  >
                    <Settings size={14} />
                    Settings
                  </DropdownMenu.Item>
                  <DropdownMenu.Item
                    onSelect={() => void logout()}
                    className="flex cursor-pointer items-center gap-2 px-3 py-2.5 text-sm text-danger outline-none data-[highlighted]:bg-danger-light"
                  >
                    <LogOut size={14} />
                    Sign out
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          </div>
        </header>

        <main key={activeOrgId} className="flex-1 p-content-sm md:p-content">
          {children}
        </main>
      </div>

      {mobileNavOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-ink/50" onClick={() => setMobileNavOpen(false)} role="presentation" />
          <div className="absolute left-0 top-0 h-full">
            <Rail
              unread={unread ?? 0}
              orgName={active?.orgName ?? ''}
              className="flex h-full"
              onNavigate={() => setMobileNavOpen(false)}
              onClose={() => setMobileNavOpen(false)}
            />
          </div>
        </div>
      )}
      <FloatingChat />
    </div>
  );
}

function Rail({
  unread,
  orgName,
  className,
  onNavigate,
  onClose,
}: {
  unread: number;
  orgName: string;
  className?: string;
  onNavigate?: () => void;
  onClose?: () => void;
}) {
  /*
   * Records appears only when the agency has shared at least one CRM area
   * with this client. The server is asked rather than the answer inferred
   * here — and the routes behind it refuse independently, so hiding the link
   * is a courtesy to the reader, never the control. The query is cached, so
   * asking from the rail costs nothing the page has not already paid.
   */
  const crmAccess = useCrmAccess();
  const navItems = CLIENT_NAV.filter(
    (item) => !item.crmArea || crmAccess.data?.[item.crmArea] === true,
  );

  return (
    <aside
      className={cn(
        'w-[228px] shrink-0 flex-col justify-between bg-auth-panel px-3 py-4',
        className,
      )}
    >
      <div className="min-h-0 flex-1 overflow-y-auto scrollbar-thin">
        <div className="mb-6 flex items-center gap-2.5 px-2">
          <Logo size={24} showWordmark wordmarkSize={18} />
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close navigation"
              className="ml-auto rounded p-1 text-white/40 hover:text-white"
            >
              <X size={16} />
            </button>
          )}
        </div>

        <nav className="flex flex-col gap-0.5">
          {navItems.map((item) => {
            const Icon = item.icon;
            const badge = item.badgeKey === 'unreadMessages' ? unread : 0;

            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={onNavigate}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-2.5 rounded-control px-2.5 py-2 text-[13px] transition-colors',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-auth-gold',
                    isActive
                      ? 'bg-auth-gold/15 font-semibold text-auth-gold'
                      : 'text-white/50 hover:bg-white/5 hover:text-white/85',
                  )
                }
              >
                <Icon size={16} className="shrink-0" />
                <span className="truncate">{item.label}</span>
                {badge > 0 && (
                  <span className="ml-auto grid h-[18px] min-w-[18px] shrink-0 place-items-center rounded-full bg-auth-gold px-1 text-[10px] font-bold text-auth-panel">
                    {badge > 99 ? '99+' : badge}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>
      </div>

      <div className="px-2 pt-4">
        <p className="truncate text-[11px] text-auth-dim">{orgName}</p>
        <p className="mt-0.5 text-[10px] text-auth-dim/70">© 2026 GoBeeVir Business OS</p>
      </div>
    </aside>
  );
}
